// src/ResetPasswordPage.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchJson } from './api';

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Kanit:wght@400;700;800;900&family=Space+Mono:wght@400;700&display=swap');
  .jkl-reset-root *,.jkl-reset-root *::before,.jkl-reset-root *::after{box-sizing:border-box;}
  .jkl-reset-root{min-height:100vh;background:#07070a;display:flex;align-items:center;justify-content:center;padding:24px;font-family:'Kanit',sans-serif;color:#f0ece4;}
  .jkl-reset-card{background:#0f0f16;border:1px solid rgba(255,255,255,0.07);border-radius:12px;padding:48px 44px;width:100%;max-width:400px;}
  .jkl-reset-logo{font-size:18px;font-weight:900;letter-spacing:0.2em;margin-bottom:6px;}
  .jkl-reset-eyebrow{font-family:'Space Mono',monospace;font-size:10px;letter-spacing:0.15em;color:#d4a550;text-transform:uppercase;margin-bottom:32px;}
  .jkl-reset-title{font-size:26px;font-weight:800;text-transform:uppercase;margin-bottom:8px;}
  .jkl-reset-sub{font-size:13px;color:#8a8a9a;margin-bottom:28px;line-height:1.5;}
  .jkl-reset-field{margin-bottom:18px;}
  .jkl-reset-field label{display:block;font-family:'Space Mono',monospace;font-size:9px;letter-spacing:0.18em;color:#8a8a9a;text-transform:uppercase;margin-bottom:8px;}
  .jkl-reset-field input{width:100%;padding:13px 16px;background:#0a0a0f;border:1px solid rgba(255,255,255,0.07);border-radius:6px;color:#f0ece4;font-family:'Kanit',sans-serif;font-size:15px;outline:none;transition:border-color 0.2s;}
  .jkl-reset-field input::placeholder{color:#2a2a38;}
  .jkl-reset-field input:focus{border-color:rgba(212,165,80,0.5);}
  .jkl-reset-error{background:rgba(248,113,113,0.08);border:1px solid rgba(248,113,113,0.25);border-radius:6px;padding:11px 14px;font-family:'Space Mono',monospace;font-size:10px;color:#f87171;margin-bottom:18px;}
  .jkl-reset-success{background:rgba(74,222,128,0.08);border:1px solid rgba(74,222,128,0.25);border-radius:6px;padding:11px 14px;font-family:'Space Mono',monospace;font-size:10px;color:#4ade80;margin-bottom:18px;line-height:1.5;}
  .jkl-reset-btn{width:100%;padding:14px;background:#d4a550;border:none;border-radius:6px;color:#07070a;font-family:'Kanit',sans-serif;font-size:14px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;cursor:pointer;transition:all 0.2s;margin-bottom:20px;display:flex;align-items:center;justify-content:center;gap:8px;}
  .jkl-reset-btn:hover:not(:disabled){background:#f0c878;transform:translateY(-1px);}
  .jkl-reset-btn:disabled{opacity:0.5;cursor:not-allowed;}
  .jkl-reset-back{font-family:'Space Mono',monospace;font-size:10px;letter-spacing:0.08em;color:#8a8a9a;text-transform:uppercase;text-align:center;}
  .jkl-reset-back button{background:none;border:none;cursor:pointer;color:#d4a550;font-family:'Space Mono',monospace;font-size:10px;letter-spacing:0.08em;text-transform:uppercase;padding:0;}
  .jkl-reset-back button:hover{color:#f0c878;}
  .jkl-reset-spinner{width:15px;height:15px;border:2px solid rgba(0,0,0,0.3);border-top-color:#07070a;border-radius:50%;animation:spin 0.7s linear infinite;}
  @keyframes spin{to{transform:rotate(360deg);}}
`;

function getTokenFromUrl() {
  const p = new URLSearchParams(window.location.search);
  return p.get('token') || '';
}

export default function ResetPasswordPage({ onGoLogin }) {
  const { t } = useTranslation();
  const [token] = useState(getTokenFromUrl);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isResetMode = !!token;

  useEffect(() => {
    if (!document.getElementById('jkl-reset-styles')) {
      const s = document.createElement('style');
      s.id = 'jkl-reset-styles';
      s.textContent = STYLES;
      document.head.appendChild(s);
    }
    return () => document.getElementById('jkl-reset-styles')?.remove();
  }, []);

  const handleForgot = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!email || !/\S+@\S+\.\S+/.test(email)) return setError(t('reset.err_email'));
    setLoading(true);
    try {
      await fetchJson('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) });
      setSuccess(t('reset.success_forgot'));
    } catch (e) {
      setError(e.message || t('reset.err_generic_forgot'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!password || password.length < 6) return setError(t('reset.err_password'));
    if (password !== confirm) return setError(t('reset.err_confirm'));
    setLoading(true);
    try {
      await fetchJson('/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, password }) });
      setSuccess(t('reset.success_reset'));
      setTimeout(() => onGoLogin?.(), 2500);
    } catch (e) {
      setError(e.message || t('reset.err_generic_reset'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="jkl-reset-root">
      <div className="jkl-reset-card">
        <div className="jkl-reset-logo">JKL</div>
        <div className="jkl-reset-eyebrow">{isResetMode ? t('reset.eyebrow_reset') : t('reset.eyebrow_forgot')}</div>
        <div className="jkl-reset-title">{isResetMode ? t('reset.title_reset') : t('reset.title_forgot')}</div>
        <p className="jkl-reset-sub">{isResetMode ? t('reset.sub_reset') : t('reset.sub_forgot')}</p>

        {error && <div className="jkl-reset-error">{error}</div>}
        {success && <div className="jkl-reset-success">{success}</div>}

        {!success && (
          <form onSubmit={isResetMode ? handleReset : handleForgot} noValidate>
            {!isResetMode && (
              <div className="jkl-reset-field">
                <label>{t('reset.label_email')}</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('reset.ph_email')} autoComplete="email" />
              </div>
            )}
            {isResetMode && (
              <>
                <div className="jkl-reset-field">
                  <label>{t('reset.label_password')}</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={t('reset.ph_password')} autoComplete="new-password" />
                </div>
                <div className="jkl-reset-field">
                  <label>{t('reset.label_confirm')}</label>
                  <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder={t('reset.ph_confirm')} autoComplete="new-password" />
                </div>
              </>
            )}
            <button type="submit" className="jkl-reset-btn" disabled={loading}>
              {loading ? <span className="jkl-reset-spinner" /> : (isResetMode ? t('reset.submit_reset') : t('reset.submit_forgot'))}
            </button>
          </form>
        )}

        <div className="jkl-reset-back">
          <button onClick={onGoLogin}>{t('reset.back')}</button>
        </div>
      </div>
    </div>
  );
}
